import { Modal, Form, Select, Input, Switch, Row, Col, Divider, DatePicker } from 'antd';
import { useEffect } from 'react';
import dayjs from 'dayjs';
import { ORAL_HYGIENE, JAW_DEVELOPMENT, JAW_WIDTH, TONGUE_MOVEMENT, MOUTH_BREATHING } from '../../../utils/constants';

// initialValues — existing checkup record; when provided the modal is in "update" mode
const CheckupForm = ({ open, onClose, onSubmit, registration, initialValues, loading }) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (!open) { return; }
    if (initialValues) {
      form.setFieldsValue({
        oralHygieneStatus: initialValues.oralHygieneStatus,
        weight:            initialValues.weight,
        height:            initialValues.height,
        hasCavities:       initialValues.hasCavities       ?? false,
        hasGumIssues:      initialValues.hasGumIssues      ?? false,
        painReported:      initialValues.painReported      ?? false,
        treatmentRequired: initialValues.treatmentRequired ?? false,
        jawDevelopment:    initialValues.jawDevelopment,
        upperJaw:          initialValues.upperJaw,
        lowerArch:         initialValues.lowerArch,
        tongueMovement:    initialValues.tongueMovement,
        mouthBreathing:    initialValues.mouthBreathing    ?? false,
        treatmentType:     initialValues.treatmentType,
        nextVisitDate:     initialValues.nextVisitDate ? dayjs(initialValues.nextVisitDate) : null,
        treatmentNotes:    initialValues.treatmentNotes,
        doctorRemarks:     initialValues.doctorRemarks,
      });
    } else {
      form.resetFields();
    }
  }, [open, initialValues, form]);

  const handleSubmit = async () => {
    const values = await form.validateFields();
    onSubmit({
      ...values,
      weight:        values.weight ? parseFloat(values.weight) : undefined,
      height:        values.height ? parseFloat(values.height) : undefined,
      nextVisitDate: values.nextVisitDate ? values.nextVisitDate.format('YYYY-MM-DD') : undefined,
      event:        registration?.event?.id    || registration?.event,
      child:        registration?.child?.id    || registration?.child,
      registration: registration?.id,
    });
  };

  const isEdit = !!initialValues;

  return (
    <Modal
      title={
        <span style={{ fontFamily: "'Fredoka One', sans-serif", color: 'var(--wl-primary)' }}>
          {isEdit ? 'Update Checkup' : 'Checkup'} — {registration?.child?.name || ''}
        </span>
      }
      open={open} onCancel={onClose} onOk={handleSubmit}
      okText={isEdit ? 'Update' : 'Save'}
      confirmLoading={loading} width={680} destroyOnClose
    >
      <div style={{
        background: '#f0f7ff', borderRadius: 10, padding: '10px 16px',
        marginBottom: 16, fontSize: 13, border: '1px solid #d6e4ff',
      }}>
        <strong>Child:</strong> {registration?.child?.name} &nbsp;|&nbsp;
        <strong>Event:</strong> {registration?.event?.title}
      </div>

      <Form form={form} layout="vertical">
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="oralHygieneStatus" label="Oral Hygiene Status" rules={[{ required: true }]}>
              <Select placeholder="Select status">
                {Object.entries(ORAL_HYGIENE).map(([k, v]) => (
                  <Select.Option key={v} value={v}>{k}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="weight"
              label="Weight (kg)"
              validateTrigger="onBlur"
              rules={[{
                validator: (_, v) => {
                  if (!v && v !== 0) return Promise.resolve();
                  if (!/^\d+(\.\d+)?$/.test(String(v).trim())) return Promise.reject(new Error('Enter a valid number (e.g. 45.5)'));
                  return Promise.resolve();
                },
              }]}
            >
              <Input placeholder="e.g. 45.5" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="height"
              label="Height (cm)"
              validateTrigger="onBlur"
              rules={[{
                validator: (_, v) => {
                  if (!v && v !== 0) return Promise.resolve();
                  if (!/^\d+(\.\d+)?$/.test(String(v).trim())) return Promise.reject(new Error('Enter a valid number (e.g. 120.5)'));
                  return Promise.resolve();
                },
              }]}
            >
              <Input placeholder="e.g. 120.5" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="hasCavities" label="Cavities" valuePropName="checked">
              <Switch checkedChildren="Yes" unCheckedChildren="No" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="hasGumIssues" label="Gum Issues" valuePropName="checked">
              <Switch checkedChildren="Yes" unCheckedChildren="No" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="painReported" label="Pain Reported" valuePropName="checked">
              <Switch checkedChildren="Yes" unCheckedChildren="No" />
            </Form.Item>
          </Col>
        </Row>

        <Divider style={{ margin: '12px 0' }}>Dental Assessment</Divider>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="jawDevelopment" label="Jaw Development">
              <Select placeholder="Select" allowClear>
                <Select.Option value="good">Good</Select.Option>
                <Select.Option value="to_be_assigned">To Be Assisted</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="upperJaw" label="Upper Jaw">
              <Select placeholder="Select" allowClear>
                <Select.Option value="wide">Wide</Select.Option>
                <Select.Option value="narrow">Narrow</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="lowerArch" label="Lower Arch">
              <Select placeholder="Select" allowClear>
                <Select.Option value="wide">Wide</Select.Option>
                <Select.Option value="narrow">Narrow</Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="tongueMovement" label="Tongue Movement">
              <Select placeholder="Select" allowClear>
                <Select.Option value="good">Good</Select.Option>
                <Select.Option value="dysfunction">Dysfunction</Select.Option>
                <Select.Option value="restricted">Restricted</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="mouthBreathing" label="Mouth Breathing" valuePropName="checked">
              <Switch checkedChildren="Yes" unCheckedChildren="No" />
            </Form.Item>
          </Col>
        </Row>

        <Divider style={{ margin: '12px 0' }}>Treatment</Divider>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="treatmentRequired" label="Treatment Required" valuePropName="checked">
              <Switch checkedChildren="Yes" unCheckedChildren="No" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="treatmentType" label="Treatment Type">
              <Input placeholder="e.g. Filling, Extraction" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="nextVisitDate" label="Next Visit">
              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" disabledDate={(d) => d && d < dayjs().startOf('day')} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="treatmentNotes" label="Treatment Notes">
          <Input.TextArea rows={2} placeholder="Treatment notes..." />
        </Form.Item>

        <Form.Item name="doctorRemarks" label="Doctor Remarks">
          <Input.TextArea rows={2} placeholder="Doctor remarks..." />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CheckupForm;
